import React, { useMemo, useContext } from 'react';
import { useApiService } from './useApiService';
import { ASYNC_API_CACHE_KEY_PREFIX } from '../constants';
import { isObject } from '../ui/utils';
import { encodeSearchConfig } from './useSearchConfig';

function getSearch(search) {
  return search ? (typeof search === 'string' ? `?search=${search}` : isObject(search) ? `?search=${encodeSearchConfig(search)}` : '') : '';
}

const ApiContext = React.createContext();
export const useApi = () => useContext(ApiContext);

export const ApiProvider = ({ auth, children }) => {
  const reportingV3 = useApiService({
    auth,
    baseUri: import.meta.env.VITE_REPORTING_BASE_URI + '/api/v3',
    cache: true,
    asyncStorageKey: ASYNC_API_CACHE_KEY_PREFIX,
  });
  const reportingV2 = useApiService({
    auth,
    baseUri: import.meta.env.VITE_REPORTING_BASE_URI + '/api/v2',
    cache: true,
    asyncStorageKey: ASYNC_API_CACHE_KEY_PREFIX,
  });
  const kalidasa = useApiService({ auth, baseUri: import.meta.env.VITE_KALIDASA_BASE_URI });

  const methods = useMemo(() => {
    return {
      // USER DATA ---------------------------------------------------------------------------------------------
      getUserData: async () => {
        return await kalidasa.get({ path: '/users/me', errorMessage: 'Failed to get user data.' });
      },
      // APPLICATION DATA ---------------------------------------------------------------------------------------------
      getApplications: async (search, unquoted = true) => {
        const s = getSearch(search);
        const q1 = s ? `${s}&` : '?';
        const query = `${q1}unquoted=${unquoted ? 'true' : 'false'}`;
        const response = await reportingV3.get({
          path: `/applications${query}`,
          errorMessage: 'Failed to get applications.',
        });
        return response;
      },
      // The Adopt from… picker (UND-987): a customer's applications, quoted or not, so a bound
      // quote's term/status/policy number can be shown and the most recently bound one found.
      getApplicationsForCustomer: async (customerId) => {
        return await reportingV3.get({
          path: `/applications?customerId=${customerId}&unquoted=false`,
          errorMessage: 'Failed to get applications for customer.',
        });
      },
      /* includeExcludedLosses is opt-in: the quote page and the Application page both pass it, so
         underwriting can see and restore a removed Skyward loss wherever the loss grid renders.
         SubmitApplicationPage does not pass it — a broker filling out a submission has no grid and
         no restore action, so a soft-excluded line would just be confusing noise there.

         `fresh` is for the reload the loss grid runs the moment a save returns: two saves land inside
         reportingV3's default cache window often enough — retrying a partly-failed save is exactly
         that — and a cached answer would hand back the application from before the write, showing the
         underwriter their committed edit as still unsaved. Only the refresh paths ask for it, so an
         ordinary page load still shares its read with anything else fetching the same application. */
      getApplicationWithId: async (applicationId, includeExcludedLosses, { fresh = false } = {}) => {
        return await reportingV3.get({
          path: `/applications/${applicationId}${includeExcludedLosses ? '?includeExcludedLosses=true' : ''}`,
          errorMessage: `Failed to get application with id: ${applicationId}`,
          ...(fresh ? { cacheValidFor: 0 } : null),
        });
      },
      // APPLICATION ACTIONS ---------------------------------------------------------------------------------------------
      submitApplication: async (application) => {
        return await reportingV3.post({ path: '/applications', body: application, errorMessage: 'Failed to create application.' });
      },
      parseCsvIntoForm: async ({ productId, agentId, file }) => {
        const formData = new FormData();
        formData.append('files', file);
        formData.append('productId', productId);
        formData.append('agentId', String(agentId));
        return await reportingV3.postForm({
          path: '/applications/parse-csv',
          formData,
          errorMessage: 'Failed to parse CSV.',
        });
      },
      uploadFileForApplication: async (applicationId, appFile) => {
        // broker uploads a binding form document
        const { id, document } = appFile;
        const formData = new FormData();
        formData.append('files', document);
        return await reportingV3.postForm({
          path: `/applications/${applicationId}/files/${id}`,
          formData,
          errorMessage: `Failed to upload document with file id: ${id} and application id: ${applicationId}`,
        });
      },
      createQuoteForApplication: async ({ id }) => {
        return await reportingV3.post({
          path: `/applications/${id}/quotes`,
          body: {},
          errorMessage: 'Failed to create quote for application.',
        });
      },
      updateApplication: async (application) => {
        const { id } = application || {};
        return await reportingV3.put({
          path: `/applications/${id}`,
          body: application,
          errorMessage: 'Failed to update application with id' + id,
        });
      },
      // Customer directory search (UND-987): the existing-customer typeahead on the submission form and
      // on the Application/Quote pages' Change control, backed by GET /customers?q=.
      searchCustomers: async (q) => {
        return await reportingV3.get({
          path: `/customers?q=${encodeURIComponent(q)}`,
          errorMessage: 'Failed to search customers.',
        });
      },
      // One customer's full record (GET /customers/{id}): name, EIN, NAICS code, contact emails,
      // mailing address, and the lat/lon of its most recent application. The submission form fills
      // and locks its client details from this once a customer is picked.
      getCustomer: async (customerId) => {
        return await reportingV3.get({
          path: `/customers/${encodeURIComponent(customerId)}`,
          errorMessage: 'Failed to load the customer.',
        });
      },
      // Changes the customer attached to an application (UND-987): an existing customerId attaches that
      // customer, null creates a new one from the application's own insured and address. Returns the
      // updated application, the same shape as GET /applications/{id}. 409 once any quote has bound.
      changeApplicationCustomer: async (applicationId, customerId) => {
        return await reportingV3.put({
          path: `/applications/${applicationId}/customer`,
          body: { customerId },
          errorMessage: 'Failed to change customer for application with id ' + applicationId,
        });
      },
      // Sets the application's complete self-reported set in one call: rows with an id belonging to
      // this application are updated, rows without an id are inserted, and existing rows absent from
      // `lossDetails` are deleted. Returns the merged grid list (self-reported + Skyward).
      putApplicationLossHistory: async (applicationId, lossDetails) => {
        return await reportingV3.put({
          path: `/applications/${applicationId}/loss_history`,
          body: lossDetails,
          errorMessage: 'Failed to save loss history for application with id ' + applicationId,
        });
      },
      // Manual corrections to Skyward-fed losses, keyed by the coverage line's claimLineKey. `fields` is
      // a { field: value } map. The key travels as a query parameter: it is carrier data ('|' separators,
      // and a claim number could carry '/'), and Tomcat rejects an encoded '/' in a path segment.
      upsertSkywardCorrections: async (applicationId, claimLineKey, fields) => {
        return await reportingV3.put({
          path: `/applications/${applicationId}/skyward_corrections?claimLineKey=${encodeURIComponent(claimLineKey)}`,
          body: fields,
          errorMessage: 'Failed to save loss corrections for application with id ' + applicationId,
        });
      },
      revertSkywardCorrections: async (applicationId, claimLineKey) => {
        return await reportingV3.delete({
          path: `/applications/${applicationId}/skyward_corrections?claimLineKey=${encodeURIComponent(claimLineKey)}`,
          errorMessage: 'Failed to revert loss corrections for application with id ' + applicationId,
        });
      },
      // Restores a single corrected field (e.g. `excluded`) without touching any other correction on
      // the claim line.
      revertSkywardCorrectionField: async (applicationId, claimLineKey, field) => {
        return await reportingV3.delete({
          path: `/applications/${applicationId}/skyward_corrections?claimLineKey=${encodeURIComponent(claimLineKey)}&field=${encodeURIComponent(
            field
          )}`,
          errorMessage: 'Failed to restore loss for application with id ' + applicationId,
        });
      },
      // Per-field diff of a corrected Skyward loss against the feed: what the feed said when the edit
      // was made, what we show now, and whether the feed has since diverged from it.
      getSkywardCorrections: async (applicationId, claimLineKey) => {
        return await reportingV3.get({
          path: `/applications/${applicationId}/skyward_corrections?claimLineKey=${encodeURIComponent(claimLineKey)}`,
          errorMessage: 'Failed to load loss corrections for application with id ' + applicationId,
        });
      },
      // Static per-product reference data, and every quote page load asks for it — the service's
      // 2-second default would re-fetch on each one.
      getLossCauses: async (productId) => {
        return await reportingV3.get({
          path: `/products/${productId}/loss_causes`,
          cacheValidFor: 300000,
          errorMessage: `Failed to get loss causes`,
        });
      },
      createLossCause: async (lossCauseData) => {
        return await reportingV3.post({
          path: `/loss_causes`,
          body: lossCauseData,
          errorMessage: 'Failed to create loss cause',
        });
      },
      deleteLossCauseWithId: async (lossCauseId) => {
        return await reportingV3.get({
          path: `/loss_causes/${lossCauseId}`,
          errorMessage: `Failed to delete loss cause with id: ${lossCauseId}`,
        });
      },
      // QUOTE DATA ---------------------------------------------------------------------------------------------
      getQuotes: async (search) => {
        return await reportingV3.get({
          path: `/quotes${getSearch(search)}`,
          errorMessage: `Failed to get quotes.`,
        });
      },
      getQuoteWithId: async (quoteId) => {
        return await reportingV3.get({
          path: `/quotes/${quoteId}`,
          errorMessage: `Failed to get quote with id: ${quoteId}`,
        });
      },
      generateWorkbook: async (quoteId, body) => {
        return await reportingV3.post({
          path: `/quotes/${quoteId}/workbook/generate`,
          body,
          errorMessage: `Failed to generate workbook with id: ${quoteId}`,
        });
      },
      generateQuoteDocs: async (quoteId) => {
        return await reportingV3.post({
          path: `/quotes/${quoteId}/docs`,
          errorMessage: `Failed to generate quote docs with id: ${quoteId}`,
        });
      },
      getWorkbook: async (quoteId) => {
        return await reportingV3.get({
          path: `/quotes/${quoteId}/workbook`,
          errorMessage: `Failed to get workbook with id: ${quoteId}`,
        });
      },
      getAdjustmentsWithQuoteId: async (quoteId) => {
        return await reportingV3.get({
          path: `/quotes/${quoteId}/adjustments`,
          errorMessage: `Failed to get adjustments from quote with id: ${quoteId}`,
        });
      },
      getRequestsWithQuoteId: async (quoteId) => {
        return await reportingV3.get({
          path: `/quotes/${quoteId}/requests`,
          errorMessage: `Failed to get requests from quote with id: ${quoteId}`,
        });
      },
      getPreviewOfAdjustmentOnQuoteId: async (quoteId, { requestId, notes, quote }) => {
        return await reportingV3.post({
          path: `/quotes/${quoteId}/adjustments?preview=true`,
          body: {
            ...(requestId ? { requestId } : null),
            notes,
            quote,
          },
          errorMessage: `Failed to apply and get adjustment preview on quote with id: ${quoteId}`,
        });
      },
      addDescriptionForQuoteWithId: async (quoteId, description = '') => {
        return await reportingV3.post({
          path: `/quotes/${quoteId}/description`,
          body: { description },
          errorMessage: `Failed to add description, "${description}", to quote with id ${quoteId}`,
        });
      },
      getMacavityForQuote: async (quoteId) => {
        return await reportingV3.get({
          path: `/macavity/quotes/${quoteId}/multi`,
          errorMessage: `Failed to get Macavity results with id: ${quoteId}`,
        });
      },
      getQuoteConcentrations: async (quoteId) => {
        return await reportingV3.get({
          path: `/quotes/${quoteId}/concentrations`,
          errorMessage: `Failed to get concentrations with quote id: ${quoteId}`,
        });
      },
      getQuoteAuthority: async (quoteId) => {
        // UND-656/UND-654: latest underwriting-authority snapshot. UND-715: this is now a pure READ
        // (recompute only on a first-ever miss server-side) — re-running is the explicit
        // evaluateQuoteAuthority below. Not cached — re-fetched on every adjustment refresh.
        return await reportingV3.get({
          path: `/quotes/${quoteId}/authority`,
          errorMessage: `Failed to get authority designation for quote id: ${quoteId}`,
          cache: false,
        });
      },
      evaluateQuoteAuthority: async (quoteId, { kick = true } = {}) => {
        // UND-715: explicit re-run (the panel's "Re-run authority checks" button). Evaluates +
        // persists a fresh snapshot server-side and kicks the async macavity -q authority pass when
        // catastrophe inputs are missing/stale; the response carries macavityKicked.
        // kick=false re-evaluates WITHOUT publishing to SNS — the panel's automatic refresh path
        // (post-macavity-completion / stale-snapshot self-heal) must never re-kick, or a permanently
        // unevaluable location (e.g. off-grid) would loop macavity runs forever.
        return await reportingV3.post({
          path: `/quotes/${quoteId}/authority/evaluate${kick ? '' : '?kick=false'}`,
          errorMessage: `Failed to re-run authority checks for quote id: ${quoteId}`,
        });
      },
      getQuoteTaxBasis: async (quoteId) => {
        // How the home state builds the taxable basis (commission in basis, dollar rounding) for the
        // fee grid's tax-row note. Not cached — re-fetched after every save/reset refresh.
        return await reportingV3.get({
          path: `/quotes/${quoteId}/tax-basis`,
          errorMessage: `Failed to get tax basis for quote id: ${quoteId}`,
          cache: false,
        });
      },
      getQuoteTaxes: async (quoteId) => {
        // (Re)computes taxes through InsCipher and returns the state's stamping wording, notes and
        // documents. Every call reaches InsCipher, so the Bind Instructions tab keeps its own copy.
        return await reportingV3.get({
          path: `/quotes/${quoteId}/taxes`,
          errorMessage: `Failed to get taxes for quote id: ${quoteId}`,
          cache: false,
        });
      },
      getQuoteAuthorityCells: async (quoteId) => {
        return await reportingV3.get({
          path: `/quotes/${quoteId}/authority/cells`,
          errorMessage: `Failed to get authority cells for quote id: ${quoteId}`,
          cache: false,
        });
      },
      getPmlSnapshots: async () => {
        return await reportingV3.get({
          path: `/pml-snapshots`,
          errorMessage: 'Failed to list PML snapshots',
          cache: false,
        });
      },
      getPmlSnapshotSummary: async (asOf) => {
        return await reportingV3.get({
          path: `/pml-snapshots/${asOf}/summary`,
          errorMessage: `Failed to get PML snapshot summary for ${asOf}`,
          cache: false,
        });
      },
      getPmlSnapshotCells: async (asOf, scaleMi) => {
        return await reportingV3.get({
          path: `/pml-snapshots/${asOf}/cells?scale=${scaleMi}`,
          errorMessage: `Failed to get PML snapshot cells for ${asOf} @ ${scaleMi}mi`,
          cache: false,
        });
      },
      getPmlSnapshotCellDetail: async (asOf, scaleMi, cellId) => {
        return await reportingV3.get({
          path: `/pml-snapshots/${asOf}/cells/${scaleMi}/${encodeURIComponent(cellId)}`,
          errorMessage: `Failed to get PML snapshot cell detail for ${cellId}`,
          cache: false,
        });
      },
      getPmlSnapshotClients: async (asOf) => {
        return await reportingV3.get({
          path: `/pml-snapshots/${asOf}/clients`,
          errorMessage: `Failed to get PML snapshot client movement for ${asOf}`,
          cache: false,
        });
      },
      getPmlLattice: async (scaleMi, bbox) => {
        return await reportingV3.get({
          path: `/pml-lattice/${scaleMi}${bbox ? `?bbox=${bbox.join(',')}` : ''}`,
          errorMessage: `Failed to get PML lattice outlines @ ${scaleMi}mi`,
          cache: false,
        });
      },
      // QUOTE ACTIONS ---------------------------------------------------------------------------------------------
      submitAdjustmentOnQuoteId: async (quoteId, { requestId, notes, quote }) => {
        return await reportingV3.post({
          path: `/quotes/${quoteId}/adjustments`,
          body: {
            ...(requestId ? { requestId } : null),
            notes,
            quote,
          },
          errorMessage: `Failed to apply adjustment on quote with id: ${quoteId}`,
        });
      },
      submitRequestOnQuoteId: async (quoteId, request) => {
        return await reportingV3.post({
          path: `/quotes/${quoteId}/requests`,
          body: { request },
          errorMessage: `Failed to make request on quote with id: ${quoteId}`,
        });
      },
      runMacavityForQuote: async (quoteId) => {
        return await reportingV3.post({
          path: `/macavity/quotes/${quoteId}`,
          body: {},
          errorMessage: `Could not run Macavity for quote with id: ${quoteId}`,
        });
      },
      releaseQuoteWithId: async (quoteId) => {
        // underwriter releases/publishes quote for broker to view pricing
        return await reportingV3.post({
          path: `/quotes/${quoteId}/release`,
          body: {},
          errorMessage: `Failed to release quote with id: ${quoteId}`,
        });
      },
      setQuotePolicyNumber: async (quoteId, policyNumber) => {
        // the carrier's policy number, for a product whose carrier issues them; saved on its own
        return await reportingV3.post({
          path: `/quotes/${quoteId}/policy_number`,
          body: { policyNumber },
          errorMessage: `Failed to set the policy number of quote with id: ${quoteId}`,
        });
      },
      acceptQuoteWithId: async (quoteId) => {
        // broker accepts quote on behalf of client and quote goes to prebound
        return await reportingV3.post({
          path: `/quotes/${quoteId}/prebind`,
          body: {},
          errorMessage: `Failed to accept quote with id: ${quoteId}`,
        });
      },
      rejectQuoteWithId: async (quoteId) => {
        // broker or underwriter rejects quote entirely.
        return await reportingV3.post({
          path: `/quotes/${quoteId}/reject`,
          body: {},
          errorMessage: `Failed to reject quote with id: ${quoteId}`,
        });
      },
      bindQuoteWithId: async (quoteId) => {
        // underwriter binds quote officially and a policy is generated which is the response returned
        return await reportingV3.post({
          path: `/quotes/${quoteId}/bind`,
          body: {},
          errorMessage: `Could not bind quote with id: ${quoteId}`,
        });
      },
      // BINDING FORMS ---------------------------------------------------------------------------------------------
      getBindingForms: async (searchConfig) => {
        return await reportingV3.get({
          path: `/binding_forms${getSearch(searchConfig)}`,
          errorMessage: `Failed to get binding forms`,
        });
      },
      getBindingFormsWithQuoteId: async (quoteId, searchConfig) => {
        return await reportingV3.get({
          path: `/public/quotes/${quoteId}/binding_forms${getSearch(searchConfig)}`,
          errorMessage: `Failed to get binding forms with quote id: ${quoteId}`,
          authenticated: false,
        });
      },
      getBindingFormWithIdForQuoteId: async (quoteId, bindingFormId) => {
        return await reportingV3.get({
          path: `/public/quotes/${quoteId}/binding_forms/${bindingFormId}`,
          errorMessage: `Failed to get binding form with id ${bindingFormId} and quote id: ${quoteId}`,
          authenticated: false,
        });
      },
      uploadBindingFormOnQuoteId: async (quoteId, bindingForm) => {
        // broker uploads a binding form document
        const { document, id } = bindingForm;
        const formData = new FormData();
        formData.append('document', document);
        return await reportingV3.postForm({
          path: `/quotes/${quoteId}/binding_forms/${id}`,
          formData,
          errorMessage: `Failed to upload document with binding form id: ${id} and quote id: ${quoteId}`,
        });
      },
      submitDetailsOnBindingForm: async (detailsObj, bindingForm, admin = false) => {
        const { finalized = false, submitterEmail = undefined, ...details } = detailsObj;
        // ^^ for PSCL data. details is object to overwrite in quote.bindingForms[i].details
        // bindingForm is the bindingForm object which should have id and quoteId
        const body = {
          details: {},
          finalized,
        };
        for (const key in details) {
          if (details[key] !== null) {
            body.details[key] = details[key];
          }
        }
        if (submitterEmail) {
          body.submitterEmail = submitterEmail;
        }
        const { id: bindingFormId, quoteId } = bindingForm;
        return await reportingV3.post({
          path: `/quotes/${quoteId}/binding_forms/${bindingFormId}/details${admin ? '/override' : ''}`,
          body,
          errorMessage: `Failed to submit details for binding form with id: ${bindingFormId} and quote id: ${quoteId}`,
          authenticated: admin,
        });
      },
      approveBindingFormWithIdForQuoteId: async (bindingFormId, quoteId) => {
        // ops or underwriter approve uploaded binding form
        return await reportingV3.post({
          path: `/quotes/${quoteId}/binding_forms/${bindingFormId}/approve`,
          body: {},
          errorMessage: `Failed to approve binding form with id: ${bindingFormId} and quote id: ${quoteId}`,
        });
      },
      rejectBindingFormWithIdForQuoteId: async (bindingFormId, quoteId, reason) => {
        // ops or underwriter approve uploaded binding form
        return await reportingV3.post({
          path: `/quotes/${quoteId}/binding_forms/${bindingFormId}/reject`,
          body: reason && typeof reason === 'string' && reason.trim().length ? { reason } : {},
          errorMessage: `Failed to reject binding form with id: ${bindingFormId} and quote id: ${quoteId}`,
        });
      },
      // POLICY DATA ---------------------------------------------------------------------------------------------
      getPolicies: async (searchConfig) => {
        return await reportingV3.get({ path: `/policies${getSearch(searchConfig)}`, errorMessage: 'Failed to get policies.' });
      },
      getPolicyWithId: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}`,
          errorMessage: `Failed to get policy with id: ${policyId}`,
          exclusive: true,
        });
      },
      getPolicyConcentrations: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/concentrations`,
          errorMessage: `Failed to get concentrations with policy id: ${policyId}`,
        });
      },
      getPolicyMonthlyReportingData: async (policyId, combinedMode = false) => {
        const actionParam = combinedMode ? '?action=uncombine' : '';
        return await reportingV3.get({
          path: `/policies/${policyId}/monthly${actionParam}`,
          errorMessage: `Failed to get monthly reporting data with policy id: ${policyId}`,
        });
      },

      getMonthlyReportingForMCE: async (policyId, mceId, combinedMode = false) => {
        const actionParam = combinedMode ? '?action=uncombine' : '';
        return await reportingV3.get({
          path: `/policies/${policyId}/mces/${mceId}/monthly${actionParam}`,
          errorMessage: `Failed to get monthly reporting data with policy id: ${policyId}`,
        });
      },
      getPolicyMCEs: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/mces`,
          errorMessage: `Failed to get MCEs data with policy id: ${policyId}`,
        });
      },
      updatePolicyMCEs: async (policyId, data) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/mces`,
          body: data,
          errorMessage: `Failed to update Policy MCEs data with policy id: ${policyId}`,
        });
      },
      getFormSchedule: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/formschedule`,
          errorMessage: `Failed to get MCEs data with policy id: ${policyId}`,
        });
      },
      downloadFormSchedule: async (policyId, data) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/formschedule`,
          body: data,
          errorMessage: `Failed to get MCEs data with policy id: ${policyId}`,
        });
      },
      saveFormSchedule: async (policyId, formdata) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/schedule-page`,
          body: formdata,
          errorMessage: `Failed to save Form data with policy id: ${policyId}`,
        });
      },
      getSavedFormSchedule: async (policyId, formdata) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/schedule-page`,
          errorMessage: `Failed to get Form data with policy id: ${policyId}`,
        });
      },
      getMCE: async (policyId, mceId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/mces/${mceId}`,
          errorMessage: `Failed to get MCE data with policy id: ${policyId}`,
        });
      },
      getMceTaxBasis: async (policyId, mceId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/mces/${mceId}/tax-basis`,
          errorMessage: `Failed to get tax basis for MCE: ${mceId}`,
          cache: false,
        });
      },
      getQuarterlyBreakdown: async (policyId, mceId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/mces/${mceId}/quarterly-breakdown`,
          errorMessage: `Failed to get quarterly breakdown for MCE: ${mceId}`,
        });
      },
      getGroups: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/groups`,
          errorMessage: `Failed to get groups data with policy id: ${policyId}`,
        });
      },
      updateGroups: async (policyId, requestBody) => {
        return await reportingV3.post({
          body: requestBody,
          path: `/policies/${policyId}/groups`,
          errorMessage: `Failed to get groups data with policy id: ${policyId}`,
        });
      },
      UpdateMCE: async (policyId, mceId, data) => {
        return await reportingV3.put({
          path: `/policies/${policyId}/mces/${mceId}`,
          body: data,
          errorMessage: `Failed to update MCEs data with policy id: ${policyId}`,
        });
      },
      executeMCE: async (policyId, mceId) => {
        return await reportingV3.put({
          path: `/policies/${policyId}/mces/${mceId}?action=execute`,
          errorMessage: `Failed to update execute MCEs data with policy id: ${policyId}`,
        });
      },
      addMCELocation: async (policyId, data) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/locations`,
          body: data,
          errorMessage: `Failed to add location data with policy id: ${policyId}`,
        });
      },

      deleteMCE: async (policyId, mceId) => {
        return await reportingV3.delete({
          path: `/policies/${policyId}/mces/${mceId}`,
          error: 'Failed to delete MCE',
        });
      },

      // POLICY ACTIONS ---------------------------------------------------------------------------------------------
      uploadPolicyAttachment: async (policyId, file) => {
        const { document } = file;
        const formData = new FormData();
        formData.append('files', document);
        return await reportingV3.postForm({
          path: `/policies/${policyId}/upload`,
          formData,
          errorMessage: `Failed to upload policy attachment`,
        });
      },
      regeneratePolicy: async (policyId, includeEndorsements) => {
        const queryParam = `?endorsements=${includeEndorsements}`;
        return await reportingV3.post({
          path: `/policies/${policyId}/policy${queryParam}`,
          errorMessage: `Failed to regenerate policy`,
        });
      },
      downloadPolicy: async (policyId, includeEndorsements) => {
        const queryParam = `?endorsements=${includeEndorsements}`;
        return await reportingV3.get({
          path: `/policies/${policyId}/download${queryParam}`,
          errorMessage: `Failed to download policy`,
        });
      },
      downloadPolicyWithEndorsements: async (policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/policy-endorsements/download`,
          body: [...requestBody],
          errorMessage: `Failed to download policy`,
        });
      },
      createRenewalQuote: async (policyId) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/renewal`,
          errorMessage: `Failed to create renewal quote`,
        });
      },
      renewAhsPolicy: async (policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/ahs-renewal`,
          body: requestBody,
          errorMessage: `Failed to renew AHS policy with id: ${policyId}`,
        });
      },
      getInferredPackage: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/inferred-package`,
          errorMessage: `Failed to infer package for policy with id: ${policyId}`,
        });
      },
      updatePolicyMonthlyReportingData: async (policyId, updatedData) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/monthly`,
          body: { ...updatedData },
          errorMessage: `Failed to update monthly on policy with id: ${policyId}`,
        });
      },
      updateNamedInsured: async (policyId, updatedData, include) => {
        const queryParam = `?include=${include}`;
        return await reportingV3.post({
          path: `/policies/${policyId}/namedInsureds${queryParam}`,
          body: updatedData,
          errorMessage: `Failed to update monthly on policy with id: ${policyId}`,
        });
      },
      createEndorsement: async (policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/endorsements`,
          body: requestBody,
          errorMessage: `Failed to add endorsement with policy id: ${policyId}`,
        });
      },
      insertMultipleEndorsements: async (policyId, requestBody, endorsementType) => {
        const queryParam = `?endorsementType=${endorsementType}`;
        return await reportingV3.post({
          path: `/policies/${policyId}/multipleEndorsements${queryParam}`,
          body: requestBody,
          errorMessage: `Failed to add endorsement with policy id: ${policyId}`,
        });
      },
      downloadEndorsement: async (policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/endorsements/download`,
          body: { ...requestBody },
          errorMessage: `Failed to download endorsement with policy id: ${policyId}`,
        });
      },
      downloadInvEndorsement: async (policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/policy-endorsements/combined`,
          body: { ...requestBody },
          errorMessage: `Failed to download endorsement with policy id: ${policyId}`,
        });
      },
      getEndorsements: async (policyId, mceId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/endorsements`,
          errorMessage: `Failed to get MCE data with policy id: ${policyId}`,
        });
      },
      updateOrdinals: async (policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/updateOrdinals`,
          body: [...requestBody],
          errorMessage: `Failed to update ordinals with policy id: ${policyId}`,
        });
      },
      createMCE: async (policyId, MCEDetail) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/mces`,
          body: { ...MCEDetail },
          errorMessage: `Failed to create MCE with policy id: ${policyId}`,
        });
      },
      downloadMCE: async (mceId, policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/mces/${mceId}/download`,
          errorMessage: `Failed to get MCEs data with policy id: ${policyId}`,
        });
      },
      supersedeMCE: async (mceId, policyId) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/mces/${mceId}/superseders`,
          errorMessage: `Failed to supersede MCE with policy id: ${policyId}`,
        });
      },
      reverseMCE: async (mceId, policyId) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/mces/${mceId}/reverse`,
          errorMessage: `Failed to Reverse MCE with policy id: ${policyId}`,
        });
      },
      deletesupersededMCE: async (mceId, policyId, supersedingMceId) => {
        return await reportingV3.delete({
          path: `/policies/${policyId}/mces/${mceId}/superseders/${supersedingMceId}`,
          errorMessage: `Failed to supersede MCE with policy id: ${policyId}`,
        });
      },
      downloadBreakdown: async (mceId, policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/mces/${mceId}/download/breakdown`,
          errorMessage: `Failed to get MCEs data with policy id: ${policyId}`,
        });
      },
      addAdjustments: async (mceId, policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/mces/${mceId}/adjustments`,
          body: { ...requestBody },
          errorMessage: `Failed to create Adjustment with mce id: ${mceId}`,
        });
      },
      updateAdjustments: async (mceId, policyId, adjustmentId, requestBody) => {
        return await reportingV3.put({
          path: `/policies/${policyId}/mces/${mceId}/adjustments/${adjustmentId}`,
          body: { ...requestBody },
          errorMessage: `Failed to update Adjustment with mce id: ${mceId}`,
        });
      },
      // CLAIMS DATA ---------------------------------------------------------------------------------------------
      getClaims: async (searchConfig, withPolicyData = false) => {
        // TODO implement search once exists in backend
        const claims = await reportingV3.get({
          path: `/claims${getSearch(searchConfig)}`,
          errorMessage: 'Failed to get claims',
        });
        if (withPolicyData && claims && Array.isArray(claims) && claims.length) {
          for (const claim of claims) {
            const policy = await reportingV3.get({
              path: `/policies/${claim.policyId}`,
              errorMessage: `Failed to get policy with id: ${claim.policyId}`,
            });
            if (policy) {
              claim.policy = policy;
              claim.location = policy.locations ? policy.locations.find((l) => l.id === claim.locationId) : null;
            }
          }
        }
        return claims;
      },
      getClaimWithId: async (claimId) => {
        return await reportingV3.get({
          path: `/claims/${claimId}`,
          errorMessage: `Failed to get claim with id: ${claimId}`,
        });
      },
      getClaimsWithPolicyId: async () => {},
      getClaimsWithLocationIdAndPolicyId: async (locationId, policyId) => {},
      // CLAIM ACTIONS ---------------------------------------------------------------------------------------------
      submitClaimForLocationIdAndPolicyId: async (locationId, policyId, claimData) => {
        // claimData = expectedDamage, incurredAt, coverageType, stormId, adjusterId
        return await reportingV3.post({
          path: `/policies/${policyId}/locations/${locationId}/claims`,
          body: claimData,
          errorMessage: `Failed to submit claim for locationId: ${locationId} under policyId: ${policyId}`,
        });
      },
      submitAnnotationOnClaimId: async () => {},
      submitAdjustmentOnClaimId: async () => {},
      acceptClaim: async () => {},
      denyClaim: async () => {},
      // FORMS DATA ---------------------------------------------------------------------------------------------
      getForms: async () => {
        return await reportingV3.get({ path: '/forms', errorMessage: 'Failed to get forms' });
      },
      getForm: async (formId) => {
        return await reportingV3.get({ path: `/forms/${formId}`, errorMessage: 'Failed to get form' });
      },
      getFormPreview: async (formId) => {
        return await reportingV3.get({
          path: `/forms/${formId}/preview`,
          accept: 'application/pdf',
          responseType: 'arraybuffer',
          errorMessage: 'Failed to get form preview',
        });
      },
      // PRODUCTS DATA ---------------------------------------------------------------------------------------------
      getProducts: async () => {
        return await reportingV3.get({ path: '/products', errorMessage: 'Failed to get products' });
      },
      getProductWithId: async (productId) => {
        return await reportingV3.get({ path: `/products/${productId}`, errorMessage: `Failed to get product with id: ${productId}` });
      },
      getCoveragesForProductId: async (productId) => {
        return await reportingV3.get({
          path: `/products/${productId}/coverages`,
          errorMessage: `Failed to get coverages for product id: ${productId}`,
        });
      },
      getContentForProductId: async (productId) => {
        return await reportingV3.get({
          path: `/products/${productId}/content`,
          errorMessage: `Failed to get content for product with Id: ${productId}`,
        });
      },
      getProductsWithContent: async () => {
        const products = await reportingV3.get({ path: '/products', errorMessage: 'Failed to get products' });
        if (products && Array.isArray(products) && products.length) {
          for (const product of products) {
            try {
              const content = await reportingV3.get({
                path: `/products/${product.id}/content`,
                errorMessage: `Failed to get content for product with Id: ${product.id}`,
              });
              product.content = content;
            } catch (err) {
              console.log(err);
            }
          }
        }
        return products;
      },
      getAllProductContent: async () => {
        const products = await reportingV3.get({ path: '/products', errorMessage: 'Failed to get products' });
        let contentItems = [];
        if (products && Array.isArray(products) && products.length) {
          for (const product of products) {
            try {
              const content = await reportingV3.get({
                path: `/products/${product.id}/content`,
                errorMessage: `Failed to get content for product with Id: ${product.id}`,
              });
              if (content && Array.isArray(content) && content.length) {
                for (const item of content) {
                  item.productName = product.name;
                  item.productType = product.productType;
                  contentItems.push(item);
                }
              }
            } catch (err) {
              console.log(err);
            }
          }
        }
        return contentItems;
      },
      getURLForContentItem: async (item) => {
        const { productId, id } = item;
        return await reportingV3.get({
          path: `/products/${productId}/content/${id}`,
          errorMessage: `Failed to get URL for content with id: ${id} and productId: ${productId}`,
        });
      },
      // PRODUCT ACTIONS ---------------------------------------------------------------------------------------------
      addContentItem: async (item) => {
        const { productId, title, description, locale = 'en', filename, file } = item;
        const content = new FormData();
        content.append('productId', productId);
        content.append('title', title);
        if (description) {
          content.append('description', description);
        }
        content.append('localeCode', locale);
        content.append('filename', filename);
        if (file) {
          content.append('file', file);
        }
        return await reportingV3.postForm({
          path: `/products/${productId}/content`,
          formData: content,
          errror: 'Failed to upload file',
        });
      },
      updateContentItem: async (item) => {
        const { id, productId, title, description, locale = 'en', filename, file } = item;
        const content = new FormData();
        content.append('productId', productId);
        content.append('title', title);
        if (description) {
          content.append('description', description);
        }
        content.append('localeCode', locale);
        content.append('filename', filename);
        if (file) {
          content.append('file', file);
        }
        return await reportingV3.putForm({
          path: `/products/${productId}/content/${id}`,
          formData: content,
          errror: 'Failed to upload file',
        });
      },
      deleteContentItem: async (item) => {
        const { id, productId } = item;
        return await reportingV3.delete({
          path: `/products/${productId}/content/${id}`,
          error: 'Failed to delete content item',
        });
      },
      // EXTRA ---------------------------------------------------------------------------------------------
      getSupportedLocales: async () => {
        return await reportingV3.get({ path: '/i18n/locales', errorMessage: 'Failed to get supported locales' });
      },
      getPlacesAutocomplete: async (input, session) => {
        const sessionToken = session ? `&sessionToken=${session}` : '';
        return await reportingV2.get({ path: `/places/autocomplete?input=${input}${sessionToken}`, error: `Failed to lookup place.` });
      },
      getPlacesDetails: async (placeId, session) => {
        const sessionToken = session ? `&sessionToken=${session}` : '';
        return await reportingV2.get({ path: `/places/details?placeId=${placeId}${sessionToken}`, error: `Failed to lookup place.` });
      },
      getAgents: async () => {
        return await reportingV3.get({
          path: `/agents`,
          errorMessage: `Failed to get agents.`,
        });
      },
      createAgent: async ({ email, fullName, brokerId, productIds }) => {
        return await reportingV3.post({
          path: `/agents`,
          body: {
            user: {
              email,
              fullName,
            },
            brokerId,
            productIds,
          },
          errorMessage: `Failed to create agent.`,
        });
      },
      editAgentWithId: async (agentId, { email, fullName, brokerId, productIds }) => {
        return await reportingV3.put({
          path: `/agents/${agentId}`,
          body: {
            email,
            fullName,
            brokerId,
            productIds,
          },
          errorMessage: `Failed to edit agent.`,
        });
      },
      getBrokers: async () => {
        return await reportingV3.get({
          path: `/brokers`,
          errorMessage: `Failed to get brokers.`,
        });
      },
      createBroker: async ({ name }) => {
        return await reportingV3.post({
          path: `/brokers`,
          body: { name },
          errorMessage: `Failed to create broker.`,
        });
      },
      editBrokerWithId: async (brokerId, { name }) => {
        return await reportingV3.put({
          path: `/brokers/${brokerId}`,
          body: { name },
          errorMessage: `Failed to edit broker.`,
        });
      },
      addOrUpdateCustomerLocation: async (quoteId, locationData, reset, fullProspectCoverage = false) => {
        const queryParam = `?reset=${reset}&fullProspectCoverage=${fullProspectCoverage}`;
        return await reportingV3.post({
          path: `/quotes/${quoteId}/location${queryParam}`,
          body: locationData,
          errorMessage: `Failed to add or update customer location`,
        });
      },

      getLocationChanges: async (locationId) => {
        return await reportingV3.get({
          path: `/locations/${locationId}/changes`,
          errorMessage: `Failed to fetch location history`,
        });
      },
      resetQuoteDetails: async (quoteId, quote) => {
        return await reportingV3.post({
          path: `/quotes/${quoteId}/reset`,
          body: {},
          errorMessage: `Failed to reset quote`,
        });
      },
      getMacavityStatus: async (quoteId, ecsArnId) => {
        const queryParam = ecsArnId ? `?ecsArnId=${ecsArnId}` : '';
        return await reportingV3.get({
          path: `/quotes/${quoteId}/job_status${queryParam}`,
          errorMessage: `Failed to get brokers.`,
        });
      },
      getPackages: async () => {
        return await reportingV3.get({
          path: `/policies/packages`,
          errorMessage: `Falied to get package.`,
        });
      },
      bindApplication: async (applicationId, requestBody) => {
        return await reportingV3.post({
          path: `/applications/${applicationId}/ahs`,
          body: requestBody,
          errorMessage: `Failed to get bind Application with id: ${applicationId}`,
        });
      },
      uploadAttachment: async (attachmentId, customerId, file) => {
        const { document } = file;
        const formData = new FormData();
        formData.append('files', document);
        const queryParam = `?customerId=${customerId}`;
        return await reportingV3.postForm({
          path: `/attachments/${attachmentId}/upload${queryParam}`,
          formData,
          errorMessage: `Failed to upload attachment`,
        });
      },
      saveAttachment: async (requestBody) => {
        return await reportingV3.post({
          path: `/attachments`,
          body: requestBody,
          errorMessage: `Failed to save attachment`,
        });
      },
      downloadAllAttachments: async (customerId) => {
        const queryParam = `?customerId=${customerId}`;
        return await reportingV3.get({
          path: `/attachments/zip${queryParam}`,
          errorMessage: `Failed to download attachments`,
        });
      },
      getAttachments: async (customerId) => {
        const queryParam = `?customerId=${customerId}`;
        return await reportingV3.get({
          path: `/attachments${queryParam}`,
          errorMessage: `Failed to get attachments`,
        });
      },
      getAttachmentTypes: async () => {
        return await reportingV3.get({
          path: `/attachments/types`,
          errorMessage: `Failed to get attachment types`,
        });
      },
      updateAttachment: async (requestBody) => {
        return await reportingV3.put({
          path: `/attachments`,
          body: requestBody,
          errorMessage: `Failed to update attachment`,
        });
      },
      deleteAttachment: async (attachmentId) => {
        return await reportingV3.delete({
          path: `/attachments/${attachmentId}`,
          errorMessage: `Failed to delete attachment`,
        });
      },
      getLocations: async (searchConfig, includeOrigin = false) => {
        const searchParams = getSearch(searchConfig);
        const originParam = includeOrigin ? '&include_origin=true' : '';

        return await reportingV3.get({
          path: `/locations${searchParams}${originParam}`,
          errorMessage: 'Failed to get locations.',
        });
      },
      updateLocation: async (location, updateBaseAddress) => {
        const queryParam = updateBaseAddress ? '?updateBaseAddress=true' : '';
        return await reportingV3.put({
          path: `/locations/${location?.id}${queryParam}`,
          body: location,
          errorMessage: 'Failed to Update locations.',
        });
      },
      getAggregationLocations: async (aggregationId, policyDate, options = {}) => {
        const params = new URLSearchParams();
        if (policyDate) params.append('policyDate', policyDate);
        if (options.quoteId) params.append('quoteId', options.quoteId);
        if (options.locationId) params.append('locationId', options.locationId);
        const query = params.toString();
        const queryParam = query ? `?${query}` : '';

        return await reportingV3.get({
          path: `/mapping/aggregation/${aggregationId}${queryParam}`,
          errorMessage: `Falied to get locations.`,
        });
      },
      saveAggregationConfiguration: async (requestBody) => {
        return await reportingV3.post({
          path: `/mapping/aggregation/configuration`,
          body: requestBody,
          errorMessage: `Failed to save aggregation configuration`,
        });
      },
      getAggregationConfigurations: async () => {
        return await reportingV3.get({
          path: `/mapping/aggregation/configurations`,
          errorMessage: `Failed to get aggregation configurations`,
        });
      },
      getAggregationQuotes: async (aggregationId) => {
        return await reportingV3.get({
          path: `/mapping/aggregation/${aggregationId}/quotes`,
          errorMessage: `Failed to get aggregation quotes`,
        });
      },
      getAggregationLocationsForPIF: async (policyDate, overlayQuoteId) => {
        const queryParam = policyDate ? `?policyDate=${policyDate}&overlayQuoteId=${overlayQuoteId}` : '';
        return await reportingV3.get({
          path: `/mapping/aggregation/pif${queryParam}`,
          errorMessage: `Failed to get PIF locations.`,
        });
      },

      getoverlayQuoteLocations: async (quoteId) => {
        return await reportingV3.get({
          path: `/mapping/${quoteId}/overlaylocations`,
          errorMessage: `Failed to get locations for quote ${quoteId}`,
        });
      },

      getOverlayOnlyLocations: async (quoteId) => {
        return reportingV3.get({
          path: `/mapping/aggregation/overlay-only?quoteId=${quoteId}`,
          errorMessage: 'Failed to fetch overlay-only locations',
        });
      },

      createLegacyMCE: async (policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/legacy`,
          body: requestBody,
          errorMessage: `Failed to create legacy MCE`,
        });
      },
      getLegacyMCE: async (policyId, mceId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/legacy/${mceId}`,
          errorMessage: `Failed to get legacy MCE with mce id: ${mceId}`,
        });
      },
      updateLegacyMCE: async (policyId, mceId, requestBody) => {
        return await reportingV3.put({
          path: `/policies/${policyId}/legacy/${mceId}`,
          body: requestBody,
          errorMessage: `Failed to update legacy MCE with mce id: ${mceId}`,
        });
      },
      executeLegacyMCE: async (policyId, mceId) => {
        return await reportingV3.put({
          path: `/policies/${policyId}/legacy/${mceId}?action=execute`,
          errorMessage: `Failed to execute legacy MCE with mce id: ${mceId}`,
        });
      },
      getPolicyLegacyMCEs: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/legacy`,
          errorMessage: `Failed to get legacy MCEs with policy Id:${policyId}`,
        });
      },
      deleteLegacyMCE: async (policyId, mceId) => {
        return await reportingV3.delete({
          path: `/policies/${policyId}/legacy/${mceId}`,
          error: 'Failed to delete Legacy MCE',
        });
      },

      generateMceWorkbook: async (policyId, mceId) => {
        const response = await reportingV3.get({
          path: `/policies/${policyId}/mces/${mceId}/download/uwwb`,
          errorMessage: `Failed to generate workbook for MCE: ${mceId}`,
        });

        const downloadUrl = response?.request?.responseURL || response?.config?.url || null;

        if (!downloadUrl) {
          throw new Error('No download URL returned from backend');
        }

        const link = document.createElement('a');
        link.href = downloadUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return true;
      },

      runMacavityForMCE: async (mceId) => {
        return await reportingV3.post({
          path: `/macavity/mce/${mceId}`,
          body: {},
          errorMessage: `Could not run Macavity for MCE with id: ${mceId}`,
        });
      },

      getMacavityForMCE: async (mceId) => {
        return await reportingV3.get({
          path: `/macavity/mce/${mceId}/multi`,
          errorMessage: `Failed to get Macavity results for MCE ${mceId}`,
        });
      },

      getMceJobStatus: async (mceId, ecsArnId = 'none') => {
        return await reportingV3.get({
          path: `/macavity/mce/${mceId}/job_status?ecsArnId=${ecsArnId}`,
          errorMessage: `Failed to get Macavity job status for MCE ${mceId}`,
        });
      },

      getBillingConfig: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/billing-config`,
          errorMessage: 'Failed to load billing config',
          cache: false,
        });
      },

      putBillingConfig: async (policyId, config) => {
        return await reportingV3.put({
          path: `/policies/${policyId}/billing-config`,
          body: config,
          errorMessage: 'Failed to update billing config',
        });
      },

      getLatestStatementCommissionRate: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/monthly-statement/commission-rate`,
          errorMessage: 'Failed to load the last statement commission rate',
          cache: false,
        });
      },

      insertMonthlyStatement: async (payload) => {
        return await reportingV3.post({
          path: `/policies/insertMonthly`,
          body: payload,
          error: 'Failed to insert monthly statement',
        });
      },

      downloadMonthlyStatement: async (policyId) => {
        const response = await reportingV3.get({
          path: `/policies/${policyId}/monthly-statement`,
          responseType: 'blob',
          returnFullResponse: true,
          error: `Failed to download monthly statement for policy Id: ${policyId}`,
        });

        return response;
      },

      downloadMonthlyStatementById: async (policyId, statementId) => {
        const response = await reportingV3.get({
          path: `/policies/${policyId}/monthly-statement/${statementId}/download`,
          error: `Failed to download monthly statement`,
        });

        return response;
      },
      deleteMonthlyStatement: async (policyId, statementId) => {
        return await reportingV3.delete({
          path: `/policies/${policyId}/monthly-statement/${statementId}`,
          errorMessage: 'Failed to delete monthly statement',
        });
      },
      getStatementJsonBlob: async (policyId, statementId, type) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/monthly-statement/${statementId}/json/${type}`,
          errorMessage: `Failed to download ${type} JSON`,
        });
      },
      getMonthlyStatementJson: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/monthly-statement/json`,
          errorMessage: 'Failed to load monthly statement JSON',
        });
      },
      getQuarterlyDates: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/monthly-statement/quarterly/dates`,
          errorMessage: 'Failed to load quarterly dates',
        });
      },
      getQuarterlyStatement: async (policyId, period) => {
        const query = period ? `?period=${period}` : '';
        return await reportingV3.get({
          path: `/policies/${policyId}/monthly-statement/quarterly${query}`,
          errorMessage: 'Failed to load quarterly statement',
        });
      },
      getAnnualStatement: async (policyId) => {
        return await reportingV3.get({
          path: `/policies/${policyId}/monthly-statement/annual`,
          errorMessage: 'Failed to load annual statement',
        });
      },
      getPerLocationStatement: async (policyId, endorsementMode = 'all') => {
        return await reportingV3.post({
          path: `/policies/${policyId}/monthly-statement/per-location`,
          body: { endorsementMode },
          errorMessage: 'Failed to load per-location statement',
        });
      },
      getMonthlyStatements: async (policyId) => {
        const response = await reportingV3.get({
          path: `/policies/monthlyStatements/${policyId}/allstatements`,
          error: `Failed to fetch monthly statements for policy Id: ${policyId}`,
        });
        return response;
      },

      executeMonthlyStatement: async (statementId) => {
        const response = await reportingV3.put({
          path: `/policies/monthlyStatements/${statementId}/execute`,
          body: {},
          error: `Failed to execute monthly statement with Id: ${statementId}`,
        });
        return response;
      },

      downloadLossHistoryCSV: async (applicationId) => {
        const response = await reportingV3.get({
          path: `/applications/${applicationId}/losses/download`,
          error: 'Failed to download the CSV',
        });

        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'loss-history.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return response.data;
      },
      getMonthlyReporting: async ({ month, startMonth, endMonth }) => {
        const hasRange = Boolean(startMonth || endMonth);
        const query = hasRange
          ? `?startMonth=${encodeURIComponent(startMonth || '')}&endMonth=${encodeURIComponent(endMonth || '')}`
          : month
          ? `?month=${encodeURIComponent(month)}`
          : '';
        return reportingV3.get({
          path: `/policies/monthly-reporting/month${query}`,
          errorMessage: hasRange
            ? `Failed to get monthly reporting for range: ${startMonth || 'N/A'} to ${endMonth || 'N/A'}`
            : month
            ? `Failed to get monthly reporting for month: ${month}`
            : `Failed to get monthly reporting`,
        });
      },

      removeEndorsementForm: async (policyId, requestBody) => {
        return await reportingV3.post({
          path: `/policies/${policyId}/removeEndorsementForm`,
          body: requestBody,
          errorMessage: `Failed to remove endorsement form for policy id: ${policyId}`,
        });
      },
    };
  }, [reportingV3, reportingV2, kalidasa]);

  return <ApiContext.Provider value={methods}>{children}</ApiContext.Provider>;
};
